import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import {
  MILESTONE_MONTHS,
  MILESTONE_YEAR_LEVEL_BY_GRADE,
  MILESTONE_YEAR_LEVELS,
  calendarOfMilestonesStrings as STRINGS,
  type Milestone,
  type MilestoneMonth,
} from '@backrs_ui/grow-ui/src/pages/SuccessSystem/calendarOfMilestones.content';
import { useFetchUserProfileInfoQuery } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';

import { Box, Button, ButtonBase, Menu, MenuItem, SxProps, Theme, Typography } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

import { useAgentContext } from './CalendarOfMilestones.agentContext';

type StyleFn = (theme: Theme) => SystemStyleObject<Theme>;

type MilestoneRowProps = Readonly<{
  month: MilestoneMonth;
  milestone: Milestone;
  isCurrent: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}>;

const MilestoneRow: React.FC<MilestoneRowProps> = ({
  month,
  milestone,
  isCurrent,
  isExpanded,
  onToggle,
}) => (
  <Box sx={rowSx}>
    <Box sx={monthLabelCellSx}>
      <Typography variant="text-xs-medium" sx={monthLabelSx}>
        {month}
      </Typography>
      {isCurrent && (
        <Typography variant="text-xs-semibold" sx={todayLabelSx}>
          {STRINGS.today}
        </Typography>
      )}
    </Box>
    <Box sx={eventCellSx}>
      {isCurrent && <Box sx={todayMarkerSx} />}
      <ButtonBase
        id={`calendar-of-milestones-${month.toLowerCase()}-event`}
        onClick={onToggle}
        aria-expanded={isExpanded}
        sx={[eventSx, isExpanded ? eventExpandedSx : eventCollapsedSx]}
      >
        <Typography variant="text-xs-semibold" className="calendar-event-title">
          {milestone.title}
        </Typography>
        {isExpanded ? (
          <>
            <Typography variant="text-xs-regular">{milestone.content}</Typography>
            <Typography variant="text-xs-semibold" className="calendar-event-title">
              {STRINGS.focusAreas}
            </Typography>
            <Box component="ul" sx={focusListSx}>
              {milestone.focus.map((focus) => (
                <Typography component="li" key={focus} variant="text-xs-regular">
                  {focus}
                </Typography>
              ))}
            </Box>
          </>
        ) : (
          <Typography variant="text-xs-regular" sx={eventPreviewSx}>
            {milestone.content}
          </Typography>
        )}
      </ButtonBase>
    </Box>
  </Box>
);

export const CalendarOfMilestones: React.FC = () => {
  const today = new Date();
  const currentMonth =
    MILESTONE_MONTHS[
      (today.getMonth() + MILESTONE_MONTHS.indexOf('January')) % MILESTONE_MONTHS.length
    ];

  const { loggedIn } = useAuth();
  const { data: profileData } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: !loggedIn,
  });
  const gradeYearLevelId =
    MILESTONE_YEAR_LEVEL_BY_GRADE[profileData?.profile?.publicProfile?.grade ?? ''];
  const gradeYearIndex = MILESTONE_YEAR_LEVELS.findIndex((level) => level.id === gradeYearLevelId);

  const [selectedYearIndex, setSelectedYearIndex] = useState<number | null>(null);
  const yearIndex = selectedYearIndex ?? Math.max(gradeYearIndex, 0);
  const [expandedMonths, setExpandedMonths] = useState<ReadonlySet<MilestoneMonth>>(
    () => new Set([currentMonth]),
  );
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const yearLevel = MILESTONE_YEAR_LEVELS[yearIndex];

  useAgentContext(yearLevel);

  const toggleMonth = (month: MilestoneMonth) =>
    setExpandedMonths((previous) => {
      const next = new Set(previous);

      if (next.has(month)) next.delete(month);
      else next.add(month);

      return next;
    });

  const selectYear = (index: number) => {
    setSelectedYearIndex(index);
    setMenuAnchor(null);
  };

  return (
    <Box id="calendar-of-milestones" sx={calendarSx}>
      <Box sx={headerSx}>
        <Box sx={headerTextSx}>
          <Box sx={dateBadgeSx}>
            <Box sx={dateBadgeMonthSx}>
              <Typography variant="text-xs-semibold">
                {currentMonth.slice(0, 3).toUpperCase()}
              </Typography>
            </Box>
            <Box sx={dateBadgeYearSx}>
              <Typography variant="text-lg-bold">{today.getFullYear()}</Typography>
            </Box>
          </Box>
          <Box sx={headerTitleSx}>
            <Typography variant="text-lg-semibold" sx={titleSx}>
              {STRINGS.title}
            </Typography>
            <Typography variant="text-sm-regular" sx={subtitleSx}>
              {STRINGS.subtitle}
            </Typography>
          </Box>
        </Box>
        <Box sx={actionsSx}>
          <Box sx={buttonGroupSx}>
            <ButtonBase
              id="calendar-of-milestones-previous-year-btn"
              aria-label={STRINGS.previousYear}
              disabled={yearIndex === 0}
              onClick={() => selectYear(yearIndex - 1)}
              sx={groupButtonSx}
            >
              <AppIcon iconName="ArrowLeft" diameter="20px" color="currentColor" />
            </ButtonBase>
            <Typography variant="text-sm-semibold" sx={yearLabelSx}>
              {yearLevel.label}
            </Typography>
            <ButtonBase
              id="calendar-of-milestones-next-year-btn"
              aria-label={STRINGS.nextYear}
              disabled={yearIndex === MILESTONE_YEAR_LEVELS.length - 1}
              onClick={() => selectYear(yearIndex + 1)}
              sx={groupButtonSx}
            >
              <AppIcon iconName="ArrowRight" diameter="20px" color="currentColor" />
            </ButtonBase>
          </Box>
          <Button
            id="calendar-of-milestones-year-view-btn"
            hierarchy="Secondary gray"
            size="md"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            endIcon={<AppIcon iconName="ChevronDown" diameter="20px" color="currentColor" />}
          >
            {STRINGS.yearView}
          </Button>
          <Menu
            id="calendar-of-milestones-year-view-menu"
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {MILESTONE_YEAR_LEVELS.map((level, index) => (
              <MenuItem
                key={level.id}
                id={`calendar-of-milestones-year-${level.id}-option`}
                selected={index === yearIndex}
                onClick={() => selectYear(index)}
              >
                {level.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      <Box>
        {MILESTONE_MONTHS.map((month) => (
          <MilestoneRow
            key={`${yearLevel.id}-${month}`}
            month={month}
            milestone={yearLevel.milestones[month]}
            isCurrent={month === currentMonth}
            isExpanded={expandedMonths.has(month)}
            onToggle={() => toggleMonth(month)}
          />
        ))}
      </Box>
    </Box>
  );
};

const calendarSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  overflow: 'hidden',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  mb: backrs.spacing['spacing-4xl'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  px: { xs: backrs.spacing['spacing-xl'], sm: backrs.spacing['spacing-3xl'] },
  py: backrs.spacing['spacing-2xl'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const headerTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
});

// Hardcoded: fixed width so the month and year labels stay centered as the year changes.
const dateBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '64px',
  overflow: 'hidden',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const dateBadgeMonthSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  pt: backrs.spacing['spacing-xs'],
  pb: backrs.spacing['spacing-xxs'],
  px: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  color: backrs.colors['Colors/Text/text-quaternary'],
});

// Hardcoded: sub-token padding that optically centers the year in the badge.
const dateBadgeYearSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  pt: '1px',
  pb: '3px',
  px: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const headerTitleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-xxs'],
  alignSelf: 'stretch',
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'block',
  color: backrs.colors['Colors/Text/text-primary'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  width: { xs: '100%', sm: 'auto' },
  whiteSpace: 'nowrap',
});

const buttonGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: { xs: '1 1 100%', sm: 'none' },
  alignItems: 'stretch',
  minHeight: '40px',
  overflow: 'hidden',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const groupButtonSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-lg'],
  color: 'inherit',
  '&:hover': { backgroundColor: backrs.colors['Colors/Background/bg-secondary'] },
  '&.Mui-disabled': { color: backrs.colors['Colors/Foreground/fg-disabled-subtle'] },
});

const yearLabelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  px: backrs.spacing['spacing-xl'],
  borderLeft: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

// Hardcoded: fixed month column so event cards align; narrower on mobile to leave room for them.
const monthLabelCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  flexShrink: 0,
  width: { xs: '72px', sm: '144px' },
  pt: backrs.spacing['spacing-md'],
  pr: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-subtle'],
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const monthLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const todayLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Foreground/fg-brand-primary-alt'],
});

const eventCellSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  p: backrs.spacing['spacing-sm'],
});

const todayMarkerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '1px',
  backgroundColor: backrs.colors['Colors/Foreground/fg-brand-primary-alt'],
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-4px',
    top: '-4px',
    width: '8px',
    height: '8px',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: backrs.colors['Colors/Foreground/fg-brand-primary-alt'],
  },
});

const eventSx: StyleFn = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xxs'],
  width: '100%',
  px: backrs.spacing['spacing-md'],
  py: backrs.spacing['spacing-sm'],
  textAlign: 'left',
  borderRadius: backrs.radius['radius-sm'],
  border: '1px solid',
});

const eventCollapsedSx: StyleFn = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  borderColor: backrs.colors['Colors/Border/border-secondary'],
  color: backrs.colors['Colors/Text/text-tertiary'],
  '& .calendar-event-title': { color: backrs.colors['Colors/Text/text-secondary'] },
});

const eventExpandedSx: StyleFn = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-brand-primary'],
  borderColor: backrs.colors['Colors/Border/border-brand-alt'],
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
  '& .calendar-event-title': { color: backrs.colors['Colors/Text/text-brand-secondary'] },
});

const eventPreviewSx: SxProps<Theme> = {
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const focusListSx: SxProps<Theme> = ({ backrs }) => ({
  m: 0,
  pl: backrs.spacing['spacing-xl'],
});
